import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Building2, GitBranch, Database, Rocket, Upload, ImageIcon,
  ChevronLeft, ChevronRight, Check, Plus, Trash2, Minus,
  Loader2, SkipForward, MapPin, Phone, Mail, Monitor,
  PackageOpen, FileSpreadsheet, Sparkles, AlertCircle, CheckCircle
} from 'lucide-react';
import { onboardingAPI, subscriptionAPI, branchesAPI } from '../../services/api';

// ── Saudi Cities ──
const SAUDI_CITIES = [
  'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام',
  'الخبر', 'الظهران', 'الطائف', 'تبوك', 'بريدة', 'عنيزة',
  'حائل', 'الجبيل', 'ينبع', 'أبها', 'خميس مشيط', 'نجران',
  'جازان', 'الباحة', 'سكاكا', 'عرعر', 'القطيف', 'الأحساء',
  'الخرج', 'القصيم', 'حفر الباطن', 'الزلفي', 'المجمعة',
];

// ── Step Definitions ──
// Profile + Branches moved up into the pre-activation OnboardingFlow.js
// (Steps 2 & 3 there). SetupWizard now handles only the post-activation
// technical setup: devices, catalog import, and launch review. The earlier
// tabs remain available as "review" sections inside the existing steps so
// owners can still tweak profile/branch details if needed.
// Post-payment setup wizard steps. Devices are now chosen BEFORE payment
// inside the main OnboardingFlow (Step 5) — the device stepper was removed
// from here to avoid surprising the user with extra charges after they've
// already paid.
const STEPS = [
  { key: 'catalog', label: 'قوالب التحاليل', icon: Database, optional: true },
  { key: 'review', label: 'جاهز للانطلاق', icon: Rocket },
];


// ── Reusable Input (OUTSIDE component to prevent re-creation) ──
const WizardInput = ({ label, value, onChange, placeholder, type = 'text', dir, required, icon: Icon, maxLength }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />}
      <input
        type={type}
        dir={dir}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`w-full ${Icon ? 'pr-10' : 'pr-4'} pl-4 py-2.5 border border-gray-200 rounded-xl text-sm
          focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all
          ${dir === 'ltr' ? 'text-left' : ''}`}
      />
    </div>
  </div>
);

// ── Reusable Select (OUTSIDE component) ──
const WizardSelect = ({ label, value, onChange, options, placeholder, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full pr-4 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm
        focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all
        bg-white appearance-none"
    >
      <option value="">{placeholder || 'اختر...'}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// ── Slide animation ──
const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
};

// ── Confetti ──
const Confetti = () => {
  const colors = ['#0F6E56', '#1D9E75', '#378ADD', '#F59E0B', '#E24B4A', '#10B981'];
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Array.from({ length: 60 }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ x: '50vw', y: '50vh', scale: 0, rotate: 0 }}
          animate={{
            x: `${Math.random() * 100}vw`,
            y: `${Math.random() * 100}vh`,
            scale: [0, 1, 0.5],
            rotate: Math.random() * 720 - 360,
          }}
          transition={{ duration: 1.5 + Math.random(), ease: 'easeOut' }}
          className="absolute w-2.5 h-2.5 rounded-sm"
          style={{ backgroundColor: colors[i % colors.length] }}
        />
      ))}
    </div>
  );
};

const SetupWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [plan, setPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Get stored data from registration
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const storedTenant = JSON.parse(localStorage.getItem('onboarding_tenant') || '{}');

  // ── Step 1: Profile ──
  const [profile, setProfile] = useState({
    nameAr: storedTenant.nameAr || storedUser.nameAr || storedUser.name || '',
    name: storedTenant.name || storedUser.name || '',
    vatNumber: '',
    crNumber: '',
    street: '',
    buildingNumber: '',
    additionalNumber: '',
    district: '',
    city: '',
    postalCode: '',
    phone: '',
    supportEmail: storedUser.email || '',
  });

  // ── Logo ──
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('حجم الملف يجب أن لا يتجاوز 2MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('يجب اختيار ملف صورة'); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogoPreview(reader.result); setLogoBase64(reader.result); };
    reader.readAsDataURL(file);
  };

  // ── Step 2: Branches ──
  const [branches, setBranches] = useState([
    { branch_name: 'الفرع الرئيسي', city: '', address: '', supervisor_name: '', supervisor_email: '', supervisor_mobile: '', isMain: true },
  ]);

  // Devices are now collected BEFORE payment (in the main OnboardingFlow
  // Step 5), so SetupWizard no longer tracks extraDevices or recomputes a
  // live price breakdown — those concerns belong to the pre-payment flow.

  // ── Step 4: Catalog ──
  const [catalogChoice, setCatalogChoice] = useState('empty');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  useEffect(() => {
    loadProgress();
    loadSubscription();
    loadTemplates();
    loadExistingBranches();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await onboardingAPI.getTemplates();
      // Backend wraps the list under { success, data: [...] } via successResponse.
      const templates = Array.isArray(res.data?.data)
        ? res.data.data
        : (Array.isArray(res.data) ? res.data : []);
      setAvailableTemplates(templates);
      // Auto-select the default template
      const defaultTpl = templates.find((t) => t.is_default);
      if (defaultTpl) setSelectedTemplateId(defaultTpl._id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SetupWizard] loadTemplates failed:', err?.response?.status, err?.response?.data || err?.message);
      setAvailableTemplates([]);
    } finally {
      setTemplatesLoaded(true);
    }
  };


  const loadSubscription = async () => {
    try {
      const res = await subscriptionAPI.getMy();
      if (res.data?.data || res.data) {
        const subData = res.data?.data || res.data;
        setSubscription(subData);
        if (subData.plan) {
          setPlan(subData.plan);
        }
      }
    } catch (err) {
      // Silent - plan data not critical for setup
      console.log('Could not load plan data');
    }
  };

  const loadProgress = async () => {
    try {
      const res = await onboardingAPI.getProgress();
      const data = res.data?.data || res.data;
      if (data?.completed_steps) {
        setCompletedSteps(new Set(data.completed_steps));
      }
    } catch {
      // Silent
    }
  };

  const loadExistingBranches = async () => {
    try {
      const res = await branchesAPI.getAll();
      const existing = res.data?.data || res.data || [];
      if (existing.length > 0) {
        setBranches(existing.map((b, idx) => ({
          _id: b._id,
          branch_name: b.branch_name || '',
          city: b.city || '',
          address: b.address || '',
          supervisor_name: b.supervisor_name || '',
          supervisor_email: b.supervisor_email || '',
          supervisor_mobile: b.supervisor_mobile || '',
          isMain: idx === 0,
        })));
      }
    } catch {
      // Silent - keep default branch entry
    }
  };

  // ── Navigation ──
  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setDirection(1);
      setCurrentStep(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (idx) => {
    setDirection(idx > currentStep ? 1 : -1);
    setCurrentStep(idx);
  };

  // ── Handlers ──
  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleBranchChange = (index, field, value) => {
    setBranches(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
  };

  const addBranch = () => {
    setBranches(prev => [...prev, {
      branch_name: '', city: '', address: '', supervisor_name: '', supervisor_email: '', supervisor_mobile: '', isMain: false,
    }]);
  };

  const removeBranch = (index) => {
    if (branches[index].isMain) return;
    setBranches(prev => prev.filter((_, i) => i !== index));
  };


  // ── Save step ──
  const saveStep = async (stepKey) => {
    setSaving(true);
    try {
      switch (stepKey) {
        case 'profile': {
          const address = [profile.street, profile.buildingNumber, profile.additionalNumber, profile.district, profile.city, profile.postalCode]
            .filter(Boolean).join('، ');
          await onboardingAPI.updateProfile({
            vatNumber: profile.vatNumber,
            crNumber: profile.crNumber,
            address,
            supportPhone: profile.phone,
            supportEmail: profile.supportEmail,
          });
          if (logoBase64) {
            await onboardingAPI.updateBranding({ logoUrl: logoBase64 });
          }
          setCompletedSteps(prev => new Set([...prev, 'profile']));
          toast.success('تم حفظ بيانات المختبر');
          break;
        }
        case 'branches': {
          const branchData = branches.map(b => ({
            branch_name: b.branch_name,
            city: b.city,
            address: b.address,
            supervisor_name: b.supervisor_name,
            supervisor_email: b.supervisor_email,
            supervisor_mobile: b.supervisor_mobile,
          }));
          await onboardingAPI.createBranches({ branches: branchData });
          setCompletedSteps(prev => new Set([...prev, 'branches']));
          toast.success('تم حفظ الفروع');
          break;
        }
        default:
          break;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
      return false;
    } finally {
      setSaving(false);
    }
    return true;
  };

  const handleNext = async () => {
    const stepKey = STEPS[currentStep].key;

    if (stepKey === 'profile') {
      if (!profile.nameAr || !profile.name) {
        toast.error('اسم المختبر مطلوب');
        return;
      }
      if (profile.vatNumber && !/^\d{15}$/.test(profile.vatNumber)) {
        toast.error('الرقم الضريبي يجب أن يكون 15 رقم');
        return;
      }
      const ok = await saveStep('profile');
      if (!ok) return;
    }

    if (stepKey === 'branches') {
      const hasEmpty = branches.some(b => !b.branch_name);
      if (hasEmpty) {
        toast.error('يرجى تعبئة اسم كل فرع');
        return;
      }
      const ok = await saveStep('branches');
      if (!ok) return;
    }

    if (stepKey === 'catalog') {
      if (catalogChoice === 'template' && selectedTemplateId) {
        setSaving(true);
        try {
          const res = await onboardingAPI.importTests(selectedTemplateId);
          const data = res.data?.data;
          toast.success(`تم استيراد ${data?.tests || ''} تحليل في ${data?.categories || ''} فئة بنجاح`);
        } catch (err) {
          if (err.response?.status === 400) {
            toast.success('الفحوصات موجودة مسبقاً');
          } else {
            toast.error(err.response?.data?.message || 'حدث خطأ أثناء الاستيراد');
            setSaving(false);
            return;
          }
        } finally {
          setSaving(false);
        }
      }
      setCompletedSteps(prev => new Set([...prev, 'catalog']));
    }

    goNext();
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await onboardingAPI.complete();
      // Refresh the cached tenant blob so `ProtectedRoute` sees the new
      // `onboardingStep: 'READY'`. Without this the blob on disk still says
      // e.g. PROFILE_COMPLETE, ProtectedRoute bounces us back to /onboarding,
      // /onboarding sees the server is ready and sends us to /, and we loop.
      try {
        const { authAPI } = await import('../../services/api');
        const profileRes = await authAPI.getProfile();
        const profileData = profileRes.data?.data || profileRes.data;
        if (profileData?.tenant) {
          localStorage.setItem('tenant', JSON.stringify(profileData.tenant));
        }
      } catch {
        // If profile refresh fails, at least patch the cached blob locally
        // so ProtectedRoute doesn't spin. The next load will reconcile.
        // We know both fields must be true at this point: complete() only
        // succeeds for an active tenant, and READY is what we just set.
        try {
          const cached = JSON.parse(localStorage.getItem('tenant') || '{}');
          cached.onboardingStep = 'READY';
          cached.accountStatus = 'active';
          localStorage.setItem('tenant', JSON.stringify(cached));
        } catch { /* ignore */ }
      }
      // Clear onboarding scratchpad so a later /onboarding visit doesn't
      // resume from a stale cached step.
      try {
        localStorage.removeItem('lis_onboarding_state');
        localStorage.removeItem('lis_onboarding_step');
        localStorage.removeItem('onboarding_tenant');
      } catch { /* ignore */ }
      setShowConfetti(true);
      toast.success('مبارك! تم تفعيل مختبرك بنجاح');
      setTimeout(() => navigate('/', { replace: true }), 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  // ══════════════════════════════
  //  STEP RENDERERS
  // ══════════════════════════════

  const renderProfileStep = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WizardInput label="اسم المختبر (عربي)" value={profile.nameAr} onChange={v => handleProfileChange('nameAr', v)} required icon={Building2} />
        <WizardInput label="Lab Name (English)" value={profile.name} onChange={v => handleProfileChange('name', v)} dir="ltr" required icon={Building2} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WizardInput label="الرقم الضريبي (VAT)" value={profile.vatNumber} onChange={v => handleProfileChange('vatNumber', v)} placeholder="15 رقم" maxLength={15} dir="ltr" />
        <WizardInput label="السجل التجاري" value={profile.crNumber} onChange={v => handleProfileChange('crNumber', v)} dir="ltr" />
      </div>

      <div className="bg-gray-50 rounded-xl p-4 space-y-4">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#0F6E56]" />
          العنوان الوطني
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardInput label="الشارع" value={profile.street} onChange={v => handleProfileChange('street', v)} />
          <WizardInput label="رقم المبنى" value={profile.buildingNumber} onChange={v => handleProfileChange('buildingNumber', v)} placeholder="4 أرقام" maxLength={4} dir="ltr" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardInput label="الرقم الإضافي" value={profile.additionalNumber} onChange={v => handleProfileChange('additionalNumber', v)} placeholder="4 أرقام" maxLength={4} dir="ltr" />
          <WizardInput label="الحي" value={profile.district} onChange={v => handleProfileChange('district', v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WizardSelect
            label="المدينة"
            value={profile.city}
            onChange={v => handleProfileChange('city', v)}
            options={SAUDI_CITIES.map(c => ({ value: c, label: c }))}
            placeholder="اختر المدينة"
          />
          <WizardInput label="الرمز البريدي" value={profile.postalCode} onChange={v => handleProfileChange('postalCode', v)} placeholder="5 أرقام" maxLength={5} dir="ltr" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WizardInput label="رقم الهاتف" value={profile.phone} onChange={v => handleProfileChange('phone', v)} icon={Phone} dir="ltr" placeholder="05XXXXXXXX" />
        <WizardInput label="البريد الإلكتروني للدعم" value={profile.supportEmail} onChange={v => handleProfileChange('supportEmail', v)} icon={Mail} dir="ltr" type="email" />
      </div>

      {/* Logo Upload */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <ImageIcon className="w-4 h-4 text-[#0F6E56]" />
          شعار المختبر
          <span className="text-xs text-gray-400 font-normal">(اختياري)</span>
        </h4>
        <div className="flex items-center gap-4">
          {logoPreview ? (
            <div className="relative">
              <img src={logoPreview} alt="Logo" className="h-16 w-auto object-contain rounded-lg border border-gray-200 bg-white p-1" />
              <button
                type="button"
                onClick={() => { setLogoPreview(null); setLogoBase64(null); }}
                className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
              >×</button>
            </div>
          ) : (
            <label className="cursor-pointer flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#0F6E56] hover:bg-[#E1F5EE]/30 transition-all">
              <Upload className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">اختر صورة الشعار</span>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </label>
          )}
          <p className="text-xs text-gray-400">PNG أو JPG، حد أقصى 2MB</p>
        </div>
      </div>
    </div>
  );

  const renderBranchesStep = () => {
    // Branches (and their pricing) are now collected in the pre-payment
    // flow, so this is legacy render logic kept only in case old links
    // still target this step. Use a static fallback for the display price.
    const branchPricePerMonth = Math.round(((plan?.monthlyPrice || 0) * (plan?.limits?.additionalBranchPricePercent || 50)) / 100);
    const includedBranches = plan?.limits?.maxBranches || 1;
    const extraBranchCount = Math.max(0, branches.length - includedBranches);
    const canAddMore = true; // Always allow — extra branches are paid

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-500">أضف فروع المختبر. الفرع الرئيسي مطلوب.</p>
          <button
            onClick={addBranch}
            disabled={!canAddMore}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              canAddMore
                ? 'bg-[#E1F5EE] text-[#0F6E56] hover:bg-[#C8EAE0]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Plus className="w-4 h-4" /> إضافة فرع
          </button>
        </div>

        {/* Pricing Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">فرع واحد مضمن مع الباقة</p>
            <p>كل فرع إضافي بـ <span className="font-bold">{branchPricePerMonth.toLocaleString('ar-SA-u-ca-gregory')} ر.س / شهر</span></p>
            {extraBranchCount > 0 && (
              <p className="mt-1 text-blue-900 font-bold">إجمالي الفروع الإضافية: {extraBranchCount} × {branchPricePerMonth.toLocaleString('ar-SA-u-ca-gregory')} = {(extraBranchCount * branchPricePerMonth).toLocaleString('ar-SA-u-ca-gregory')} ر.س/شهر</p>
            )}
          </div>
        </div>

        {/* Info note */}
        {branches.length > 1 && (
          <div className="text-xs text-gray-600 px-3 py-2 bg-gray-50 rounded-lg">
            فرع مضمن + {extraBranchCount} فرع إضافي — يمكنك إضافة المزيد لاحقاً
          </div>
        )}

        {branches.map((branch, idx) => {
          const isBranchAdditional = idx > 0;
          return (
            <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-[#0F6E56]" />
                  <span className="text-sm font-semibold text-gray-700">
                    {branch.isMain ? 'الفرع الرئيسي' : `فرع ${idx}`}
                  </span>
                  {branch.isMain && <span className="text-[10px] bg-[#E1F5EE] text-[#0F6E56] px-2 py-0.5 rounded-full font-medium">رئيسي</span>}
                  {isBranchAdditional && (
                    <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                      +{branchPricePerMonth.toLocaleString('ar-SA-u-ca-gregory')} ر.س/شهر
                    </span>
                  )}
                </div>
                {!branch.isMain && (
                  <button onClick={() => removeBranch(idx)} className="text-red-400 hover:text-red-600 transition-colors p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <WizardInput label="اسم الفرع" value={branch.branch_name} onChange={v => handleBranchChange(idx, 'branch_name', v)} required />
                <WizardSelect label="المدينة" value={branch.city} onChange={v => handleBranchChange(idx, 'city', v)} options={SAUDI_CITIES.map(c => ({ value: c, label: c }))} placeholder="اختر المدينة" />
                <div className="md:col-span-2">
                  <WizardInput label="العنوان" value={branch.address} onChange={v => handleBranchChange(idx, 'address', v)} icon={MapPin} />
                </div>
                <WizardInput label="اسم المشرف" value={branch.supervisor_name} onChange={v => handleBranchChange(idx, 'supervisor_name', v)} />
                <WizardInput label="جوال المشرف" value={branch.supervisor_mobile} onChange={v => handleBranchChange(idx, 'supervisor_mobile', v)} icon={Phone} dir="ltr" placeholder="05XXXXXXXX" />
                <div className="md:col-span-2">
                  <WizardInput label="إيميل المشرف" value={branch.supervisor_email} onChange={v => handleBranchChange(idx, 'supervisor_email', v)} icon={Mail} dir="ltr" type="email" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const UNIT_SYSTEM_INFO = {
    conventional: { icon: '🇺🇸', label: 'وحدات تقليدية', sublabel: 'mg/dL, ng/mL, g/dL', desc: 'تُستخدم في السعودية وأمريكا والشرق الأوسط' },
    si: { icon: '🌍', label: 'وحدات دولية', sublabel: 'mmol/L, µmol/L, nmol/L', desc: 'تُستخدم في أوروبا وكندا وأستراليا' },
  };

  const renderCatalogStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 mb-4">اختر طريقة إعداد كتالوج الفحوصات</p>
      {!templatesLoaded && (
        <div className="flex items-center justify-center py-8 text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin me-2" /> جارٍ تحميل القوالب المتاحة…
        </div>
      )}
      {templatesLoaded && availableTemplates.length === 0 && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          تعذّر تحميل قوالب الفحوصات الجاهزة. يمكنك المتابعة بكتالوج فارغ والبدء من الصفر،
          وستتمكّن من استيراد قوالب لاحقاً من إعدادات الفحوصات.
        </div>
      )}
      <div className={`grid grid-cols-1 ${availableTemplates.length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
        {/* Empty catalog option */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => { setCatalogChoice('empty'); setSelectedTemplateId(null); }}
          className={`cursor-pointer rounded-xl border-2 p-6 text-center transition-all ${
            catalogChoice === 'empty' ? 'border-[#1D9E75] bg-[#E1F5EE] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          {catalogChoice === 'empty' && (
            <div className="flex justify-end mb-2">
              <div className="w-5 h-5 bg-[#1D9E75] rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
            </div>
          )}
          <PackageOpen className="w-12 h-12 mx-auto mb-3 text-[#0F6E56]" />
          <h4 className="font-bold text-gray-800 mb-2">البدء بكتالوج فارغ</h4>
          <p className="text-xs text-gray-500 leading-relaxed">ابدأ من الصفر وأضف الفحوصات يدوياً</p>
        </motion.div>

        {/* Template options - dynamically from API */}
        {availableTemplates.map(tpl => {
          const info = UNIT_SYSTEM_INFO[tpl.unit_system] || { icon: '', label: tpl.name, sublabel: '', desc: '' };
          const isSelected = catalogChoice === 'template' && selectedTemplateId === tpl._id;
          return (
            <motion.div
              key={tpl._id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => { setCatalogChoice('template'); setSelectedTemplateId(tpl._id); }}
              className={`cursor-pointer rounded-xl border-2 p-6 text-center transition-all ${
                isSelected ? 'border-[#1D9E75] bg-[#E1F5EE] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {isSelected && (
                <div className="flex justify-end mb-2">
                  <div className="w-5 h-5 bg-[#1D9E75] rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                </div>
              )}
              <FileSpreadsheet className={`w-12 h-12 mx-auto mb-3 ${isSelected ? 'text-[#0F6E56]' : 'text-gray-400'}`} />
              <h4 className="font-bold text-gray-800 mb-1">{info.label}</h4>
              <p className="text-xs text-gray-400 font-mono mb-2">{info.sublabel}</p>
              <p className="text-xs text-gray-500 leading-relaxed mb-2">{info.desc}</p>
              <div className="text-xs text-[#0F6E56] font-medium">
                +{tpl.stats?.total_tests || 0} فحص مع هوامش مرجعية جاهزة
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const includedDevices = plan?.limits?.maxDevices || 3;
    const checkItems = [
      { label: 'بيانات المختبر', done: completedSteps.has('profile'), key: 'profile' },
      { label: 'الفروع', done: completedSteps.has('branches'), key: 'branches' },
      { label: 'الأجهزة', done: completedSteps.has('devices') || true, key: 'devices' },
      { label: 'البيانات الأولية', done: true, key: 'catalog', optional: true },
    ];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-[#0F6E56]" /><h4 className="text-sm font-semibold text-gray-700">المختبر</h4></div>
            <p className="text-sm text-gray-800 font-medium">{profile.nameAr || '---'}</p>
            <p className="text-xs text-gray-500 mt-1" dir="ltr">{profile.name || '---'}</p>
            {profile.vatNumber && <p className="text-xs text-gray-500 mt-1">ضريبي: {profile.vatNumber}</p>}
            {profile.city && <p className="text-xs text-gray-500 mt-1">{profile.city} - {profile.district}</p>}
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-3"><GitBranch className="w-4 h-4 text-green-500" /><h4 className="text-sm font-semibold text-gray-700">الفروع</h4></div>
            <p className="text-2xl font-bold text-gray-800">{branches.length}</p>
            <p className="text-xs text-gray-500">{branches.map(b => b.branch_name).filter(Boolean).join('، ') || '---'}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-3"><Monitor className="w-4 h-4 text-purple-500" /><h4 className="text-sm font-semibold text-gray-700">الأجهزة</h4></div>
            <p className="text-2xl font-bold text-gray-800">{includedDevices}</p>
            <p className="text-xs text-gray-500">حسب باقتك</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">قائمة الإعداد</h4>
          <div className="space-y-2">
            {checkItems.map(item => (
              <div key={item.key} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className={`text-sm ${item.done ? 'text-gray-800' : 'text-gray-400'}`}>
                  {item.label}
                  {item.optional && <span className="text-xs text-gray-400 mr-1">(اختياري)</span>}
                </span>
              </div>
            ))}
          </div>
        </div>

        <motion.button
          onClick={handleComplete}
          disabled={saving}
          whileHover={{ scale: saving ? 1 : 1.02 }}
          whileTap={{ scale: saving ? 1 : 0.97 }}
          className="w-full py-4 bg-[#1D9E75] text-white rounded-xl font-bold text-lg shadow-xl shadow-[#1D9E75]/25 hover:shadow-[#1D9E75]/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
        >
          {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> جاري التفعيل...</> : <><Rocket className="w-5 h-5" /> ابدأ الآن!</>}
        </motion.button>
      </div>
    );
  };

  const renderStep = () => {
    // Index map after devices was moved to the pre-payment flow:
    //   0 = catalog, 1 = review.
    switch (currentStep) {
      case 0: return renderCatalogStep();
      case 1: return renderReviewStep();
      default: return null;
    }
  };

  const currentStepDef = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-[#F1EFE8]/40 font-[Cairo]" dir="rtl" style={{ lineHeight: 1.7 }}>
      {showConfetti && <Confetti />}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0F6E56] rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-[#2C2C2A]">إعداد المختبر</h1>
                <p className="text-xs text-[#888780]">أكمل الخطوات التالية لتفعيل مختبرك</p>
              </div>
            </div>
            <span className="text-sm text-gray-400 font-medium">{currentStep + 1} / {STEPS.length}</span>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-1">
            {STEPS.map((step, idx) => {
              const isActive = idx === currentStep;
              const isDone = idx < currentStep || completedSteps.has(step.key);
              return (
                <React.Fragment key={step.key}>
                  <button
                    onClick={() => goToStep(idx)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                      isActive ? 'bg-[#E1F5EE] text-[#0F6E56]' : isDone ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-[#0F6E56] text-white' : isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isDone && !isActive ? <Check className="w-3.5 h-3.5" /> : <span className="text-[11px] font-bold">{idx + 1}</span>}
                    </div>
                    <span className="hidden md:inline">{step.label}</span>
                  </button>
                  {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded ${isDone ? 'bg-green-400' : 'bg-gray-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E1F5EE] flex items-center justify-center">
                <currentStepDef.icon className="w-5 h-5 text-[#0F6E56]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">{currentStepDef.label}</h2>
                {currentStepDef.optional && <span className="text-xs text-gray-400">خطوة اختيارية - يمكنك التخطي</span>}
              </div>
            </div>
          </div>

          <div className="p-6 min-h-[400px]">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          {!isLastStep && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={goPrev}
                disabled={currentStep === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" /> السابق
              </button>
              <div className="flex items-center gap-2">
                {currentStepDef.optional && (
                  <button onClick={goNext} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
                    <SkipForward className="w-4 h-4" /> تخطي
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-[#0F6E56] rounded-xl hover:bg-[#1D9E75] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري الحفظ...</> : <>التالي <ChevronLeft className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
